import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { PauseOctagon, Play, RefreshCw, RotateCcw } from "lucide-react";

function fmtCents(cents) {
  const n = Number(cents || 0);
  return `$${(n / 100).toFixed(4)}`;
}

const PLANS = ["free", "team", "team_plus", "grandfathered"];

export default function WorkspaceDetailDrawer({
  workspaceId,
  onClose,
  onChanged,
}) {
  const open = Boolean(workspaceId);
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [plan, setPlan] = useState("");
  const [markup, setMarkup] = useState("");
  const [trialCents, setTrialCents] = useState("");
  const [prepaidCents, setPrepaidCents] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/workspaces/${id}`);
      setWs(data);
      setPlan(data.plan || "free");
      setMarkup(String(data.markup_rate_percent ?? ""));
      setTrialCents(String(data.trial_anthropic_cents_remaining ?? ""));
      setPrepaidCents(String(data.prepaid_balance_cents ?? ""));
      setReason("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (workspaceId) load(workspaceId);
  }, [workspaceId, load]);

  const saveChanges = async () => {
    if (!ws) return;
    if (reason.trim().length < 3) {
      toast.error("Provide a reason (3+ characters) — audit requires it.");
      return;
    }
    const payload = { reason: reason.trim() };
    if (plan !== ws.plan) payload.plan = plan;
    const newMarkup = Number(markup);
    if (
      !Number.isNaN(newMarkup) &&
      newMarkup !== Number(ws.markup_rate_percent)
    ) {
      payload.markup_rate_percent = Math.round(newMarkup);
    }
    const newTrial = Number(trialCents);
    if (
      !Number.isNaN(newTrial) &&
      newTrial !== Number(ws.trial_anthropic_cents_remaining)
    ) {
      payload.trial_anthropic_cents_remaining = newTrial;
    }
    const newPrepaid = Number(prepaidCents);
    if (
      !Number.isNaN(newPrepaid) &&
      newPrepaid !== Number(ws.prepaid_balance_cents)
    ) {
      payload.prepaid_balance_cents = newPrepaid;
    }
    if (Object.keys(payload).length === 1) {
      toast("No changes to save.", { description: "Edit a field first." });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/workspaces/${ws.id}`,
        payload
      );
      setWs(data);
      setReason("");
      toast.success("Workspace updated.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspend = async () => {
    if (!ws) return;
    const newState = !ws.suspended;
    const r = window.prompt(
      newState
        ? "Reason for suspending this workspace? (will be shown to members)"
        : "Reason for resuming this workspace? (audit log only)"
    );
    if (!r || r.trim().length < 3) {
      toast.error("Reason required (3+ chars).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/workspaces/${ws.id}`, {
        suspended: newState,
        suspended_reason: newState ? r.trim() : null,
        reason: r.trim(),
      });
      setWs(data);
      toast.success(newState ? "Workspace suspended." : "Workspace resumed.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetMarkup = async () => {
    if (!ws) return;
    const r = window.prompt(
      "Reset markup to plan default? Provide a reason (audit log):"
    );
    if (!r || r.trim().length < 3) {
      toast.error("Reason required (3+ chars).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/workspaces/${ws.id}/reset-markup`,
        { reason: r.trim() }
      );
      setWs(data);
      setMarkup(String(data.markup_rate_percent ?? ""));
      toast.success(`Markup reset to ${data.markup_rate_percent}%.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const [lastSyncReport, setLastSyncReport] = useState(null);

  const syncFromStripe = async () => {
    if (!ws) return;
    const r = window.prompt(
      "Pull this workspace's subscription state + missed top-ups directly from " +
        "Stripe and reconcile.\n\nProvide a reason (audit log):"
    );
    if (!r || r.trim().length < 3) {
      toast.error("Reason required (3+ chars).");
      return;
    }
    const creditOk = window.confirm(
      "Also retroactively credit any PAID one-off top-ups that never made it " +
        "through the webhook?\n\nOK = credit them.\nCancel = report-only (no credit)."
    );
    setSyncing(true);
    try {
      const { data } = await api.post(
        `/admin/workspaces/${ws.id}/sync-stripe`,
        { reason: r.trim(), credit_recovered_topups: creditOk }
      );
      setWs(data);
      setMarkup(String(data.markup_rate_percent ?? ""));
      setTrialCents(String(data.trial_anthropic_cents_remaining ?? ""));
      setPrepaidCents(String(data.prepaid_balance_cents ?? ""));
      setLastSyncReport(data.sync_report || null);
      const rep = data.sync_report || {};
      const recovered = (rep.topups_recovered || []).length;
      const skipped = (rep.topups_skipped_already_credited || []).length;
      const subPart = rep.subscription_synced
        ? "subscription mirrored"
        : "no active subscription";
      const recPart = recovered
        ? `${recovered} top-up${recovered === 1 ? "" : "s"} recovered`
        : skipped
        ? `0 recovered (${skipped} already credited)`
        : "no missed top-ups";
      toast.success(`Synced from Stripe — ${subPart}, ${recPart}.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto bg-[#FAF9F7]"
        data-testid="workspace-detail-drawer"
      >
        {loading || !ws ? (
          <div className="font-sans text-sm text-[#7A7571] py-8">
            Loading workspace…
          </div>
        ) : (
          <>
            <SheetHeader className="pb-4 border-b border-[#DCD7CE]">
              <SheetTitle
                className="font-serif text-2xl text-[#2C2A29] tracking-tight"
                data-testid="workspace-detail-name"
              >
                {ws.name}
              </SheetTitle>
              <SheetDescription className="font-sans text-xs text-[#7A7571] flex items-center gap-2">
                <code className="font-mono">{ws.slug}</code>
                <span>&middot;</span>
                <span>owner: {ws.owner_email || "—"}</span>
                <span>&middot;</span>
                <span>{ws.member_count} members</span>
                {ws.suspended ? (
                  <span
                    data-testid="workspace-detail-suspended-badge"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#8C3A1F]/10 text-[#8C3A1F] ml-1"
                  >
                    <PauseOctagon size={10} strokeWidth={2.5} />
                    Suspended
                  </span>
                ) : null}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Lifetime stats */}
              <div className="grid grid-cols-2 gap-3">
                <Stat
                  label="Lifetime raw cost"
                  value={fmtCents(ws.lifetime?.raw_cost_cents)}
                  testid="ws-detail-stat-cost"
                />
                <Stat
                  label="Lifetime tokens"
                  value={`${ws.lifetime?.input_tokens || 0} in / ${
                    ws.lifetime?.output_tokens || 0
                  } out`}
                  testid="ws-detail-stat-tokens"
                />
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={toggleSuspend}
                  disabled={saving}
                  data-testid="workspace-detail-suspend-btn"
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm border transition-colors disabled:opacity-50 ${
                    ws.suspended
                      ? "border-[#3A5A40] text-[#3A5A40] bg-white hover:bg-[#3A5A40]/10"
                      : "border-[#8C3A1F] text-[#8C3A1F] bg-white hover:bg-[#8C3A1F]/10"
                  }`}
                >
                  {ws.suspended ? (
                    <>
                      <Play size={14} strokeWidth={2} />
                      Resume workspace
                    </>
                  ) : (
                    <>
                      <PauseOctagon size={14} strokeWidth={2} />
                      Suspend workspace
                    </>
                  )}
                </button>
                <button
                  onClick={resetMarkup}
                  disabled={saving}
                  data-testid="workspace-detail-reset-markup-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm border border-[#DCD7CE] text-[#2C2A29] bg-white hover:bg-[#EFEAE0] transition-colors disabled:opacity-50"
                >
                  <RotateCcw size={14} strokeWidth={2} />
                  Reset markup to plan default
                </button>
                <button
                  onClick={syncFromStripe}
                  disabled={saving || syncing}
                  data-testid="workspace-detail-sync-stripe-btn"
                  title="Pull subscription + missed top-ups from Stripe."
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm border border-[#7A4A2C] text-[#7A4A2C] bg-white hover:bg-[#7A4A2C]/10 transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    size={14}
                    strokeWidth={2}
                    className={syncing ? "animate-spin" : ""}
                  />
                  {syncing ? "Syncing from Stripe…" : "Sync from Stripe"}
                </button>
              </div>

              {/* Phase 12.x — Stripe sync report */}
              {lastSyncReport ? (
                <div
                  className="rounded-xl border border-[#7A4A2C]/30 bg-[#FFF6F0] p-4"
                  data-testid="workspace-detail-sync-report"
                >
                  <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A4A2C] font-semibold mb-3">
                    Last Stripe sync
                  </h3>
                  <ul className="space-y-1.5 font-sans text-xs text-[#2C2A29]">
                    <li>
                      Customer:{" "}
                      <span className="font-mono">
                        {lastSyncReport.customer_resolved
                          ? `resolved (${lastSyncReport.customer_lookup})`
                          : "not found"}
                      </span>
                    </li>
                    <li>
                      Subscription:{" "}
                      <span className="font-mono">
                        {lastSyncReport.subscription_synced
                          ? lastSyncReport.subscription_id || "synced"
                          : "no active subscription on Stripe"}
                      </span>
                    </li>
                    <li>
                      Top-ups recovered:{" "}
                      <span className="font-mono">
                        {(lastSyncReport.topups_recovered || []).length}
                      </span>
                      {(lastSyncReport.topups_recovered || []).length > 0 ? (
                        <ul
                          className="mt-1 pl-3 space-y-0.5 text-[#7A7571]"
                          data-testid="workspace-detail-sync-recovered-list"
                        >
                          {(lastSyncReport.topups_recovered || []).map((t) => (
                            <li key={t.session_id} className="font-mono">
                              + {fmtCents(t.amount_cents)} ·{" "}
                              <span className="text-[#A5A29E]">
                                {t.session_id}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                    <li>
                      Already-credited top-ups skipped:{" "}
                      <span className="font-mono">
                        {
                          (
                            lastSyncReport.topups_skipped_already_credited ||
                            []
                          ).length
                        }
                      </span>
                    </li>
                    <li>
                      Invoice status synced:{" "}
                      <span className="font-mono">
                        {lastSyncReport.invoice_status_synced ? "yes" : "no"}
                      </span>
                    </li>
                    {lastSyncReport.suspension_lifted ? (
                      <li className="text-[#3A5A40] font-medium">
                        Suspension lifted (payment_failed was the only reason).
                      </li>
                    ) : null}
                    {(lastSyncReport.warnings || []).length > 0 ? (
                      <li className="text-[#8C3A1F] mt-1">
                        Warnings:
                        <ul className="pl-3 list-disc">
                          {(lastSyncReport.warnings || []).map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      </li>
                    ) : null}
                  </ul>
                </div>
              ) : null}

              {/* Edit fields */}
              <div className="rounded-xl border border-[#DCD7CE] bg-white p-4 space-y-4">
                <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A7571] font-semibold">
                  Edit
                </h3>
                <Field
                  label="Plan"
                  htmlFor="ws-detail-plan"
                >
                  <select
                    id="ws-detail-plan"
                    data-testid="workspace-detail-plan-select"
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
                  >
                    {PLANS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Markup (%)"
                  htmlFor="ws-detail-markup"
                  hint="Customer multiplier on raw Anthropic cost"
                >
                  <input
                    id="ws-detail-markup"
                    data-testid="workspace-detail-markup-input"
                    type="number"
                    min={0}
                    max={1000}
                    value={markup}
                    onChange={(e) => setMarkup(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 font-mono"
                  />
                </Field>
                <Field
                  label="Trial credit (cents)"
                  htmlFor="ws-detail-trial"
                  hint="Remaining trial Anthropic cents"
                >
                  <input
                    id="ws-detail-trial"
                    data-testid="workspace-detail-trial-input"
                    type="number"
                    min={0}
                    value={trialCents}
                    onChange={(e) => setTrialCents(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 font-mono"
                  />
                </Field>
                <Field
                  label="Prepaid balance (cents)"
                  htmlFor="ws-detail-prepaid"
                  hint="Used after trial is exhausted"
                >
                  <input
                    id="ws-detail-prepaid"
                    data-testid="workspace-detail-prepaid-input"
                    type="number"
                    value={prepaidCents}
                    onChange={(e) => setPrepaidCents(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 font-mono"
                  />
                </Field>
                <Field
                  label="Reason for changes"
                  htmlFor="ws-detail-reason"
                  hint="Required — recorded in the audit log."
                >
                  <input
                    id="ws-detail-reason"
                    data-testid="workspace-detail-reason-input"
                    type="text"
                    placeholder="e.g. customer support ticket #1234"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
                  />
                </Field>
                <button
                  onClick={saveChanges}
                  disabled={saving}
                  data-testid="workspace-detail-save-btn"
                  className="w-full px-4 py-2.5 rounded-md font-sans text-sm font-medium bg-[#2C2A29] text-white hover:bg-[#1A1918] transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>

              {/* Members */}
              <div className="rounded-xl border border-[#DCD7CE] bg-white p-4">
                <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A7571] font-semibold mb-3">
                  Members ({ws.members?.length || 0})
                </h3>
                {(ws.members || []).map((m) => (
                  <div
                    key={m.user_id}
                    data-testid={`ws-detail-member-${m.user_id}`}
                    className="flex items-center justify-between py-1.5 border-b border-[#EFEAE0] last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="font-sans text-sm text-[#2C2A29] truncate">
                        {m.display_name || m.email}
                      </div>
                      <div className="font-sans text-xs text-[#7A7571] truncate">
                        {m.email} &middot; {m.role}
                      </div>
                    </div>
                    <div className="font-mono text-xs text-[#7A7571]">
                      {fmtCents(m.lifetime_raw_cost_cents)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent audit */}
              {ws.recent_audit?.length ? (
                <div className="rounded-xl border border-[#DCD7CE] bg-white p-4">
                  <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A7571] font-semibold mb-3">
                    Recent admin actions
                  </h3>
                  <ul className="space-y-2">
                    {ws.recent_audit.map((a) => (
                      <li
                        key={a.id}
                        data-testid={`ws-detail-audit-${a.id}`}
                        className="font-sans text-xs text-[#7A7571] border-l-2 border-[#DCD7CE] pl-2"
                      >
                        <span className="font-mono text-[#2C2A29]">
                          {a.action}
                        </span>{" "}
                        by {a.actor_email || "?"} &middot;{" "}
                        {a.created_at
                          ? new Date(a.created_at).toLocaleString()
                          : ""}
                        {a.payload?.reason ? (
                          <div className="text-[#A5A29E] italic mt-0.5">
                            “{a.payload.reason}”
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-[#DCD7CE] bg-white p-3"
    >
      <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-1">
        {label}
      </p>
      <p className="font-mono text-sm text-[#2C2A29]">{value}</p>
    </div>
  );
}

function Field({ label, hint, htmlFor, children }) {
  return (
    <div>
      <Label
        htmlFor={htmlFor}
        className="font-sans text-xs text-[#2C2A29] font-medium mb-1.5 block"
      >
        {label}
      </Label>
      {children}
      {hint ? (
        <p className="font-sans text-[11px] text-[#A5A29E] mt-1">{hint}</p>
      ) : null}
    </div>
  );
}
